import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { stockService } from "@/features/stocks/services/stock.service";
import type { StockResponseDTO } from "@/features/stocks/types/stock.types";

interface StockSummary {
  id: number;
  barcode?: string;
  photoUrl?: string | null;
}

interface StockMultiSelectFieldProps {
  value: number[];
  onChange: (ids: number[]) => void;
  /** Resumo das peças já vinculadas — usado para preencher os chips. */
  initialItems?: StockSummary[];
  disabled?: boolean;
  inputId?: string;
}

const DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 8;

function renderChipLabel(item: StockSummary): ReactNode {
  const codePart = item.barcode ? item.barcode : `#${item.id}`;
  return (
    <span className="inline-flex items-center gap-1.5">
      {item.photoUrl ? (
        <img
          src={item.photoUrl}
          alt=""
          className="w-4 h-4 rounded object-cover"
        />
      ) : null}
      <span>{codePart}</span>
    </span>
  );
}

// A API não expõe getById para o role USER — peças sem busca prévia
// são exibidas apenas com #id.
export function StockMultiSelectField({
  value,
  onChange,
  initialItems,
  disabled = false,
  inputId,
}: StockMultiSelectFieldProps) {
  const [details, setDetails] = useState<Map<number, StockSummary>>(() => {
    const map = new Map<number, StockSummary>();
    initialItems?.forEach((item) => map.set(item.id, item));
    return map;
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<StockResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!initialItems?.length) return;
    // Popula details com itens pré-carregados vindos por prop.
    // Sincronização com prop externa — legítimo.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDetails((prev) => {
      const next = new Map(prev);
      initialItems.forEach((item) => {
        if (!next.has(item.id)) next.set(item.id, item);
      });
      return next;
    });
  }, [initialItems]);

  const fetchStocks = useCallback(async (term: string, signal: AbortSignal) => {
    const response = await stockService.list(
      { barcode: [term.trim()], pageSize: SEARCH_PAGE_SIZE },
      { signal },
    );
    return response.content;
  }, []);

  useDebouncedSearch({
    term: searchTerm,
    fetcher: fetchStocks,
    setItems: setResults,
    setLoading: setSearching,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar estoques")),
    debounceMs: DEBOUNCE_MS,
  });

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleAdd = useCallback(
    (stock: StockResponseDTO) => {
      if (value.includes(stock.id)) return;
      setDetails((prev) => {
        const next = new Map(prev);
        next.set(stock.id, {
          id: stock.id,
          barcode: stock.barcode,
          photoUrl: stock.photoUrl,
        });
        return next;
      });
      onChange([...value, stock.id]);
      setSearchTerm("");
      setResults([]);
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (id: number) => {
      onChange(value.filter((current) => current !== id));
    },
    [value, onChange],
  );

  const visibleResults = results.filter((stock) => !value.includes(stock.id));

  return (
    <div ref={containerRef} className="relative">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((id) => {
            const item = details.get(id) ?? { id };
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-0.5 text-xs"
              >
                {renderChipLabel(item)}
                <button
                  type="button"
                  onClick={() => handleRemove(id)}
                  disabled={disabled}
                  aria-label={`Remover peça ${item.barcode ?? `#${id}`}`}
                  className="rounded-full hover:bg-secondary-foreground/10 p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          id={inputId}
          value={searchTerm}
          placeholder="Buscar peça pelo código de barras…"
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          className="pl-9"
        />
      </div>

      {open && searchTerm.trim().length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto z-50">
          {searching ? (
            <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4" />
              Buscando…
            </div>
          ) : visibleResults.length === 0 ? (
            <div className="p-3 text-center text-sm text-muted-foreground">
              Nenhuma peça encontrada
            </div>
          ) : (
            visibleResults.map((stock) => (
              <button
                key={stock.id}
                type="button"
                onClick={() => handleAdd(stock)}
                className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
              >
                {stock.barcode} · {stock.productName} · {stock.size}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
